import React from "react";
import {Box, TextField} from "@mui/material";
import {IconButton, Typography, useTheme} from "@mui/joy";
import {
    CheckRounded,
    DeleteRounded,
    DownloadFilledOutlined,
    EditRounded,
    ChatRounded,
    ImageFilledRounded,
    ImageRounded,
} from "@mui-symbols-material/w300";
import {useTranslation} from "react-i18next";
import Tooltip from '@mui/material/Tooltip';
import moment from 'moment';
import {FileListItem} from "../FileList";
import FileThumbnail from "../FileThumbnail";
import {useFileEntryActions} from "../../hooks/useFileEntryActions";
import {NodeImageTarget} from "../../hooks/useNodeImage";
import {formatBytes} from "../../utils/formatBytes";
import ConfirmDeleteModal from "../ConfirmDeleteModal";

interface FileListEntryProps {
    file: FileListItem
    onOpen: (file: FileListItem) => void
    /** Enables "use as image" for image files of this node. */
    nodeImageTarget?: NodeImageTarget
}

const FileListEntry = ({file, onOpen, nodeImageTarget}: FileListEntryProps) => {
    const {t} = useTranslation();
    const theme = useTheme();
    const access = file.access;

    const {
        editing,
        setEditing,
        filename,
        setFilename,
        comment,
        setComment,
        handleSaveEdit,
        handleDownload,
        deleteConfirmOpen,
        requestDeleteFile,
        cancelDeleteFile,
        handleDeleteFile,
        deleting,
        fileDisplayName,
        showComment,
        canSetAsNodeImage,
        isCurrentNodeImage,
        handleToggleNodeImage,
        nodeImageActionLabel,
        nodeImageBusy,
    } = useFileEntryActions(file, nodeImageTarget);

    const handleOpen = () => {
        if (editing) return;
        onOpen(file);
    };

    if (!access?.read) {
        return null;
    }

    return (
        <Box
            data-testid="file-list-entry"
            sx={{
                p: "12px",
                display: "flex",
                alignItems: "center",
                borderBottom: `1px solid ${theme.palette.divider}`,
                minHeight: "65px",
                width: "100%",
                gap: "21px",
            }}
        >
            {/* Thumbnail (klickbar zum Öffnen der Datei) */}
            <Box
                onClick={editing ? undefined : handleOpen}
                sx={{ display: 'flex', flexShrink: 0, cursor: editing ? 'default' : 'pointer' }}
            >
                <FileThumbnail file={file} />
            </Box>

            {/* Linker flexibler Textbereich (Titel) */}
            <Box
                onClick={editing ? undefined : handleOpen}
                sx={{ flexGrow: 1, flexBasis: 0, minWidth: 0, cursor: editing ? 'default' : 'pointer' }}
            >
                {editing ? (
                    <Box sx={{display: 'flex', flexDirection: {lg:'column', xl: 'row'}, gap: '8px'}}>
                        <TextField
                            label={t('File name')}
                            size="small"
                            slotProps={{ inputLabel: { shrink: true } }}
                            value={filename}
                            onChange={(e) => setFilename(e.target.value)}
                            fullWidth
                        />

                        {file.supportsComment && (
                            <TextField
                                label={t('Comment')}
                                variant="outlined"
                                size="small"
                                slotProps={{ inputLabel: { shrink: true } }}
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                fullWidth
                            />
                        )}
                    </Box>
                ) : (
                    <Box>
                        <Tooltip title={filename} placement="bottom-start" arrow={false} enterDelay={1000}>
                            <Typography
                                level="titleSmallLight"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {filename}
                            </Typography>
                        </Tooltip>
                        <Box sx={{display: 'flex', gap: '8px'}}>
                            <Typography
                                level="captionBold"
                                sx={{
                                    color: "#666",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {fileDisplayName}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Kommentar-Spalte (eigene 50%-Spalte, linksbündig, nur auf großen Bildschirmen) */}
            {!editing && (
                <Box
                    onClick={handleOpen}
                    sx={{ display: { xs: 'none', xl: 'flex' }, flexGrow: 1, flexBasis: 0, minWidth: 0, alignItems: 'center', cursor: 'pointer' }}
                >
                    {showComment && (
                        <Tooltip title={file.comment} placement="top" arrow>
                            <Typography
                                level="titleSmallLight"
                                sx={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {file.comment}
                            </Typography>
                        </Tooltip>
                    )}
                </Box>
            )}


            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', flexShrink: 0, minWidth: 0 }}>

                {/* OBERE REIHE: Kommentar-Icon UND Icons (dadurch auf exakt gleicher Höhe!) */}
                <Box sx={{ display: "flex", alignItems: "center", width: "100%", justifyContent: "flex-end" }}>

                    {/* Das Kommentar-Icon (wird ausgeblendet, sobald die Kommentar-Spalte auf xl erscheint) */}
                    {!editing && showComment && (
                        <Tooltip
                            title={file.comment}
                            placement="top"
                            arrow
                        >
                            <IconButton
                                variant="plain"
                                tabIndex={-1}
                                sx={{
                                    mr: "4px",
                                    cursor: "default",
                                    "&:hover": { backgroundColor: "transparent" },
                                    "&:active": { backgroundColor: "transparent" },
                                    display: { xs: 'none', md: 'flex', xl: 'none' } // Verhindert, dass Text UND Icon auf xl sichtbar sind
                                }}
                            >
                                <ChatRounded sx={{ color: theme.palette.text.secondary }} />
                            </IconButton>
                        </Tooltip>
                    )}

                    {/* 3. Die Aktions-Icons */}
                    {!editing && canSetAsNodeImage && (
                        <Tooltip title={nodeImageActionLabel} placement="top">
                            <IconButton
                                variant="plain"
                                color="neutral"
                                onClick={handleToggleNodeImage}
                                disabled={nodeImageBusy}
                                aria-pressed={isCurrentNodeImage}
                                data-testid="file-use-as-image"
                                sx={{ mr: "4px" }}
                            >
                                {/* Active state stays subtle: only the glyph fills, in the
                                    app's tonal primary (same as the node-type tile icon). */}
                                {isCurrentNodeImage
                                    ? <ImageFilledRounded sx={{ color: '#BD4200' }} />
                                    : <ImageRounded />}
                            </IconButton>
                        </Tooltip>
                    )}

                    {access?.write && (
                        <IconButton
                            variant="plain"
                            onClick={editing ? handleSaveEdit : () => setEditing(true)}
                            sx={{ mr: "4px" }}
                        >
                            {editing ? <CheckRounded /> : <EditRounded />}
                        </IconButton>
                    )}

                    <IconButton
                        variant="plain"
                        onClick={handleDownload}
                        sx={access?.delete ? { mr: "4px" } : {}}
                    >
                        <DownloadFilledOutlined />
                    </IconButton>

                    {access?.delete && (
                        <IconButton variant="plain" onClick={requestDeleteFile}>
                            <DeleteRounded />
                        </IconButton>
                    )}
                </Box>

                {/* UNTERE REIHE: Datum und Dateigröße */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'flex-end',
                        gap: '8px',
                        pr: '8px' // Sorgt dafür, dass der Text bündig unter den Icons aufhört
                    }}
                >
                    <Typography
                        level="captionBold"
                        sx={{ color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {file.insert ? moment(file.insert).format('L') : ''}
                    </Typography>
                    <Typography
                        level="captionBold"
                        sx={{ color: "#666", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                    >
                        {formatBytes(file.size)}
                    </Typography>
                </Box>
            </Box>
            {deleteConfirmOpen && (
                <ConfirmDeleteModal
                    open={deleteConfirmOpen}
                    title={t("Delete file")}
                    message={t("Are you sure you want to delete the file {{name}}? It is removed from this node and cannot be restored.", {name: fileDisplayName})}
                    loading={deleting}
                    onConfirm={handleDeleteFile}
                    onClose={cancelDeleteFile}
                />
            )}
        </Box>
    );
}

export default FileListEntry;
